import React from "react";
import { Alert, Card, Divider, Space, Steps, Tag, Typography } from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import SolicitacaoReservaStatusTag, {
  type StatusSolicitacaoReservaFront,
} from "../components/SolicitacaoReservaStatusTag";
import type { ResumoEstadiaByTokenResponse } from "../../../services/solicitacaoReservaService";
import { formatDate } from "../../../uteis/dateUtils";

const { Title, Text, Paragraph } = Typography;

type Props = {
  resumo: ResumoEstadiaByTokenResponse | null;
  formaPagamento: "PIX" | "CARTAO" | null;
  statusSolicitacao: StatusSolicitacaoReservaFront | null;
};

const moeda = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

function getCurrentTimelineStep(status: StatusSolicitacaoReservaFront | null) {
  switch (status) {
    case "TAXA_PAGA":
      return 1;
    case "EM_VERIFICACAO":
      return 2;
    case "RESERVA_CONFIRMADA":
      return 3;
    case "RESERVA_FINALIZADA":
      return 4;
    case "RESERVA_CANCELADA":
      return 4;
    default:
      return 1;
  }
}

function getStatusMessage(status: StatusSolicitacaoReservaFront | null) {
  switch (status) {
    case "EM_VERIFICACAO":
      return {
        type: "info" as const,
        title: "Solicitação em verificação",
        description:
          "Estamos validando disponibilidade e andamento da sua solicitação junto aos proprietários compatíveis.",
      };

    case "RESERVA_CONFIRMADA":
      return {
        type: "success" as const,
        title: "Reserva confirmada",
        description:
          "Sua reserva foi confirmada. O próximo passo é seguir com o pagamento do saldo restante dentro do prazo informado.",
      };

    case "RESERVA_FINALIZADA":
      return {
        type: "success" as const,
        title: "Processo finalizado",
        description:
          "Sua solicitação foi concluída com sucesso.",
      };

    case "RESERVA_CANCELADA":
      return {
        type: "warning" as const,
        title: "Solicitação encerrada",
        description:
          "Esta solicitação foi cancelada ou encerrada. Se necessário, uma nova solicitação deverá ser iniciada.",
      };

    default:
      return {
        type: "info" as const,
        title: "Acompanhamento da solicitação",
        description:
          "Acompanhe abaixo o andamento do seu processo.",
      };
  }
}

export default function StepAcompanhamentoSolicitacao({
  resumo,
  formaPagamento,
  statusSolicitacao,
}: Props) {
  const timelineStep = getCurrentTimelineStep(statusSolicitacao);
  const statusMessage = getStatusMessage(statusSolicitacao);

  const isCancelada = statusSolicitacao === "RESERVA_CANCELADA";
  const isConfirmada = statusSolicitacao === "RESERVA_CONFIRMADA";
  const isFinalizada = statusSolicitacao === "RESERVA_FINALIZADA";

  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      <Card
        bordered={false}
        style={{ borderRadius: 16, boxShadow: "0 8px 24px rgba(0,0,0,0.08)" }}
        styles={{ body: { padding: 24 } }}
      >
        <Space direction="vertical" size={18} style={{ width: "100%" }}>
          <div>
            <SolicitacaoReservaStatusTag status={statusSolicitacao} />
          </div>

          <Space direction="vertical" size={6} style={{ width: "100%" }}>
            <Title level={2} style={{ margin: 0 }}>
              Acompanhamento da solicitação
            </Title>
            <Text type="secondary" style={{ fontSize: 16 }}>
              Aqui você acompanha o andamento da sua solicitação de reserva.
            </Text>
          </Space>

          <Alert
            type={statusMessage.type}
            showIcon
            message={statusMessage.title}
            description={statusMessage.description}
            style={{ borderRadius: 14 }}
          />

          <Card
            bordered={false}
            style={{ borderRadius: 14, background: "rgba(0,0,0,0.02)" }}
            styles={{ body: { padding: 18 } }}
          >
            <Space direction="vertical" size={14} style={{ width: "100%" }}>
              <Title level={4} style={{ margin: 0 }}>
                Andamento
              </Title>

              <Steps
                direction="vertical"
                current={timelineStep}
                items={[
                  { title: "Solicitação iniciada" },
                  { title: "Taxa paga" },
                  { title: "Em verificação" },
                  { title: "Reserva confirmada" },
                  {
                    title: isCancelada
                      ? "Solicitação encerrada"
                      : "Processo concluído",
                  },
                ]}
              />
            </Space>
          </Card>

          {resumo && (
            <Card
              bordered={false}
              style={{ borderRadius: 14, background: "rgba(0,0,0,0.02)" }}
              styles={{ body: { padding: 18 } }}
            >
              <Space direction="vertical" size={10} style={{ width: "100%" }}>
                <Title level={4} style={{ margin: 0 }}>
                  Dados da solicitação
                </Title>

                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <Text type="secondary">Empreendimento</Text>
                  <Text strong>{resumo.empreendimento?.nome ?? "—"}</Text>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <Text type="secondary">Período</Text>
                  <Text strong>
                    {formatDate(resumo.checkin)} → {formatDate(resumo.checkout)}
                  </Text>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <Text type="secondary">Noites</Text>
                  <Text strong>{resumo.noites ?? "—"}</Text>
                </div>

                <Divider style={{ margin: "6px 0" }} />

                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <Text type="secondary">Taxa de verificação</Text>
                  <Text strong>{moeda(resumo.taxaCondicional ?? 0)}</Text>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <Text type="secondary">Forma de pagamento preferida</Text>
                  <Text strong>
                    {formaPagamento === "PIX"
                      ? "PIX"
                      : formaPagamento === "CARTAO"
                      ? "Cartão"
                      : "Não informada"}
                  </Text>
                </div>

                {isConfirmada && (
                  <>
                    <Divider style={{ margin: "6px 0" }} />
                    <Tag color="green" style={{ borderRadius: 999, width: "fit-content" }}>
                      Reserva confirmada
                    </Tag>
                    <Text>
                      O pagamento do saldo restante deverá seguir as condições informadas
                      no fluxo de confirmação.
                    </Text>
                  </>
                )}

                {isFinalizada && (
                  <>
                    <Divider style={{ margin: "6px 0" }} />
                    <Tag color="green" style={{ borderRadius: 999, width: "fit-content" }}>
                      Processo concluído
                    </Tag>
                  </>
                )}

                {isCancelada && (
                  <>
                    <Divider style={{ margin: "6px 0" }} />
                    <Tag color="red" style={{ borderRadius: 999, width: "fit-content" }}>
                      Solicitação encerrada
                    </Tag>
                  </>
                )}
              </Space>
            </Card>
          )}

          {statusSolicitacao === "EM_VERIFICACAO" && (
            <Alert
              type="info"
              showIcon
              icon={<ClockCircleOutlined />}
              message="Aguardando retorno da verificação"
              description="Enquanto a análise estiver em andamento, os dados da solicitação permanecem bloqueados para edição."
              style={{ borderRadius: 14 }}
            />
          )}

          {statusSolicitacao === "RESERVA_CONFIRMADA" && (
            <Alert
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
              message="Reserva confirmada"
              description="Agora o próximo passo é cumprir o fluxo financeiro da confirmação."
              style={{ borderRadius: 14 }}
            />
          )}

          {statusSolicitacao === "RESERVA_CANCELADA" && (
            <Alert
              type="warning"
              showIcon
              icon={<CloseCircleOutlined />}
              message="Solicitação encerrada"
              description="Os dados foram preservados apenas para acompanhamento histórico."
              style={{ borderRadius: 14 }}
            />
          )}

          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Nesta área, o objetivo é acompanhar. As alterações deixam de ficar
            disponíveis para preservar a consistência do processo.
          </Paragraph>
        </Space>
      </Card>
    </div>
  );
}
